/**
 * The OAuth consent request a signed-out user was on, kept across the login round trip,
 * and the one validator every consent `returnTo` goes through.
 *
 * The general deep link (`pendingDeepLink` in AuthProvider) lives in memory, so a reload on
 * the login page — or a TOTP typo that makes someone start over — loses it. For most of the
 * console that costs a click. For OAuth consent it strands the user: the AI app is waiting
 * on a request id nobody can type back in. So this one destination is kept in
 * sessionStorage (tab-scoped, gone when the tab closes) and consumed exactly once.
 *
 * Only the request id is stored, never a path, and the path is rebuilt from it here — so
 * nothing else writing to the key can turn it into a redirect somewhere else.
 */
export const PENDING_CONSENT_KEY = 'tepco.pendingConsent';

/** A consent request id as the backend issues it (a UUID) — and nothing that could carry a path. */
const REQUEST_ID = /^[A-Za-z0-9_-]+$/;

/**
 * The only shape a consent `returnTo` may take: a same-origin path, exactly
 * `/oauth/consent?request=<id>`. No scheme, no host, no `//`, no second parameter,
 * no fragment, no percent-encoding — anything else is ignored rather than "cleaned".
 */
const CONSENT_RETURN = /^\/oauth\/consent\?request=[A-Za-z0-9_-]+$/;

export function isConsentReturnPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && CONSENT_RETURN.test(path);
}

export function consentPath(requestId: string): string {
  return `/oauth/consent?request=${requestId}`;
}

/** The request id of a consent URL (`pathname + search`), or `null` for anything else. */
export function consentRequestId(path: string): string | null {
  return isConsentReturnPath(path) ? path.slice(path.indexOf('=') + 1) : null;
}

function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Remembers the consent request behind `path`; a no-op for any other path. */
export function savePendingConsent(path: string): void {
  const id = consentRequestId(path);
  if (!id) return;
  try {
    store()?.setItem(PENDING_CONSENT_KEY, id);
  } catch {
    /* storage full or blocked: the in-memory deep link still covers this tab */
  }
}

/** Reads and clears the stored request; returns its consent path, or `null`. */
export function takePendingConsent(): string | null {
  const s = store();
  if (!s) return null;
  try {
    const id = s.getItem(PENDING_CONSENT_KEY);
    s.removeItem(PENDING_CONSENT_KEY);
    return id && REQUEST_ID.test(id) ? consentPath(id) : null;
  } catch {
    return null;
  }
}

/** Forgets the stored request — called once a decision is made, so nothing replays it. */
export function clearPendingConsent(): void {
  try {
    store()?.removeItem(PENDING_CONSENT_KEY);
  } catch {
    /* nothing to clear */
  }
}
